import { allowCookies, optimizelySDKKey } from 'utils/settings';
import { experiments } from './experiments';
import { FeatureName } from './features';
import { getCookie } from 'utils/cookies';
import { logEvent } from 'helpers/analytics';

let optimizelyClientInstance: any;

export let activeExperiments: {
    [key: string]: string | null;
} = {};

/**
 * Gets the user ID from the '_ga' cookie.
 * Overrides it with the value of 'optimizely_user' if found in the query parameters.
 */
export const getUserId = () => {
    const userId = getCookie('_ga');
    const userIdOverride = new URLSearchParams(window.location.search).get('optimizely_user');
    return userIdOverride || userId;
};
/**
 * Returns the browser name.
 * Taken from https://developer.mozilla.org/en-US/docs/Web/API/Window/navigator
 *
 * @returns { string }
 */
const getBrowserName = () => {
    const sUsrAg = navigator.userAgent;
    let sBrowser;

    // The order matters here, and this may report false positives for unlisted browsers.
    if (sUsrAg.indexOf('Firefox') > -1) {
        sBrowser = 'Mozilla Firefox';
        // "Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:61.0) Gecko/20100101 Firefox/61.0"
    } else if (sUsrAg.indexOf('SamsungBrowser') > -1) {
        sBrowser = 'Samsung Internet';
        // "Mozilla/5.0 (Linux; Android 9; SAMSUNG SM-G955F Build/PPR1.180610.011) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/9.4 Chrome/67.0.3396.87 Mobile Safari/537.36
    } else if (sUsrAg.indexOf('Opera') > -1 || sUsrAg.indexOf('OPR') > -1) {
        sBrowser = 'Opera';
        // "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.102 Safari/537.36 OPR/57.0.3098.106"
    } else if (sUsrAg.indexOf('Trident') > -1) {
        sBrowser = 'Microsoft Internet Explorer';
        // "Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; Zoom 3.6.0; wbx 1.0.0; rv:11.0) like Gecko"
    } else if (sUsrAg.indexOf('Edge') > -1) {
        sBrowser = 'Microsoft Edge';
        // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299"
    } else if (sUsrAg.indexOf('Chrome') > -1) {
        sBrowser = 'Google Chrome or Chromium';
        // "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Ubuntu Chromium/66.0.3359.181 Chrome/66.0.3359.181 Safari/537.36"
    } else if (sUsrAg.indexOf('Safari') > -1) {
        sBrowser = 'Apple Safari';
        // "Mozilla/5.0 (iPhone; CPU iPhone OS 11_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/11.0 Mobile/15E148 Safari/604.1 980x1306"
    } else {
        sBrowser = 'unknown';
    }
    return sBrowser;
};

/**
 * Returns the device type.
 * Taken from Taken from https://dev.to/itsabdessalam/detect-current-device-type-with-javascript-490j
 *
 * @returns { string }
 */
const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return 'tablet';
    }
    if (
        /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
    ) {
        return 'mobile';
    }
    return 'desktop';
};

export const getAttributes = () => {
    return {
        market: window?.location?.pathname?.split('/')?.[1].toUpperCase() || '',
        browser: getBrowserName() || '',
        device: getDeviceType() || '',
        isLoggedIn: !!getCookie('idp_reguser') || false,
    };
};

export const shouldAllowOptimizely = () => {
    if (!optimizelySDKKey) {
        return false;
    }
    if (experiments.length === 0) {
        return false;
    }
    if (window.location.hostname === 'localhost') {
        return true;
    }
    return allowCookies() && !!getUserId();
};

const logExperimentActivation = (activateObject: any) => {
    const experimentId = activateObject?.experiment?.id ?? '';
    const variationId = activateObject?.variation?.id ?? '';

    // log Optimizely health metrics event
    track('step 3 - logOptimizelyExperimentActivation');

    // log event to Google Analytics
    logEvent('optimizely', 'user bucketed', `(${experimentId}):(${variationId})`);
};

export const initializeOptimizely = () => {
    if (shouldAllowOptimizely()) {
        const sdk = window?.ikea?.navigation?.optimizely?.sdk;
        const datafile = window?.ikea?.navigation?.optimizely?.dataFiles?.[optimizelySDKKey!];

        if (sdk && datafile) {
            optimizelyClientInstance = sdk.createInstance({ datafile });
            optimizelyClientInstance?.notificationCenter?.addNotificationListener(
                window?.ikea?.navigation?.optimizely?.sdk?.enums.NOTIFICATION_TYPES.ACTIVATE,
                logExperimentActivation
            );
            activateExperiments();
        }
    }
};

/**
 * Activates a single experiment.
 *
 * @param experimentName
 */
export const activateExperiment = (experimentName: string) => {
    if (!optimizelyClientInstance || activeExperiments[experimentName]) {
        return;
    }
    activeExperiments[experimentName] = optimizelyClientInstance.activate(experimentName, getUserId(), getAttributes());
};
/**
 * Activates experiments where 'activateOnPageLoad' is 'true' on page load.
 */
export const activateExperiments = () => {
    if (!optimizelyClientInstance || experiments.length === 0) {
        return;
    }
    experiments.forEach(experiment => {
        if (experiment.activateOnPageLoad) {
            activateExperiment(experiment.name);
        }
    });
};

/**
 * Returns the variation where visitor will be bucketed.
 * If the visitor is in the experiment, this function returns the variation name.
 * If the visitor is NOT in the experiment, this function returns 'null'.
 *
 * @param experimentName
 * @returns { string | null }
 */
export const getVariation = (experimentName: string) =>
    optimizelyClientInstance?.getVariation(experimentName, getUserId(), getAttributes()) ?? null;

/**
 * Returns whether 'featureName' is enabled.
 *
 * @param featureName
 * @returns { boolean }
 */
export const isFeatureEnabled = (featureName: FeatureName) =>
    optimizelyClientInstance?.isFeatureEnabled(featureName, getUserId(), getAttributes()) ?? false;

/**
 * Tracks 'eventKey'.
 * Optional: Custom tags.
 *
 * @param eventKey
 * @param tags
 */
export const track = (eventKey: string, tags: { [key: string]: string | number | boolean } = {}) =>
    optimizelyClientInstance?.track(eventKey, getUserId(), getAttributes(), tags);
